import eleve from '../../img/symbols/eleve.svg';
import leger from '../../img/symbols/leger.svg';
import faible from '../../img/symbols/faible.svg';
import modere from '../../img/symbols/modere.svg';
import tresEleve from '../../img/symbols/tres-eleve.svg';

/**
 * Advanced Symbol Legend Manager
 * Handles symbol legends for Active Substances, Treatments, and Products
 * Based on symbol LABELS only - values are ignored for display
 */
class SymbolLegendManager {
  constructor() {
    this.symbolMappings = {
      health: {
        acute_toxicity: {
          category: 'health',
          name: 'Acute Toxicity',
          key: 'acute_toxicity_symbol_data',
        },
        long_term_effect: {
          category: 'health',
          name: 'Long Term Effects',
          key: 'long_term_effect_symbol_data',
        },
      },
      environment: {
        fishes: {
          category: 'environment',
          name: 'Fish/Daphnia',
          key: 'fishes_symbol_data',
        },
        birds: {
          category: 'environment',
          name: 'Birds',
          key: 'birds_symbol_data',
        },
        bees: {
          category: 'environment',
          name: 'Bees',
          key: 'bees_symbol_data',
        },
        persistence: {
          category: 'environment',
          name: 'Persistence',
          key: 'persistence_symbol_data',
        },
        leaching: {
          category: 'environment',
          name: 'Leaching',
          key: 'leaching_symbol_data',
        },
      },
    };

    this.labelMappings = {
      none: { label: 'Aucun', image: faible, color: '#22c55e', order: 1 },
      low: { label: 'Faible', image: faible, color: '#22c55e', order: 1 },
      slight: { label: 'Léger', image: leger, color: '#84cc16', order: 2 },
      moderate: { label: 'Modéré', image: modere, color: '#eab308', order: 3 },
      high: { label: 'Élevé', image: eleve, color: '#f97316', order: 4 },
      extremely_high: {
        label: 'Extrêmement élevé',
        image: tresEleve,
        color: '#dc2626',
        order: 5,
      },
      aucun: { label: 'Aucun', image: faible, color: '#22c55e', order: 1 },
      faible: { label: 'Faible', image: faible, color: '#22c55e', order: 1 },
      léger: { label: 'Léger', image: leger, color: '#84cc16', order: 2 },
      modéré: { label: 'Modéré', image: modere, color: '#eab308', order: 3 },
      élevé: { label: 'Élevé', image: eleve, color: '#f97316', order: 4 },
      'extrêmement élevé': {
        label: 'Extrêmement élevé',
        image: tresEleve,
        color: '#dc2626',
        order: 5,
      },
      1: { label: 'Faible', image: faible, color: '#22c55e', order: 1 },
      2: { label: 'Léger', image: leger, color: '#84cc16', order: 2 },
      3: { label: 'Modéré', image: modere, color: '#eab308', order: 3 },
      4: { label: 'Élevé', image: eleve, color: '#f97316', order: 4 },
      5: {
        label: 'Extrêmement élevé',
        image: tresEleve,
        color: '#dc2626',
        order: 5,
      },
    };
  }

  /**
   * Get all symbol types for a specific category
   * @param {string} category - 'health' or 'environment'
   * @returns {Array} Array of symbol type objects
   */
  getSymbolsByCategory(category) {
    if (!this.symbolMappings[category]) {
      console.warn(`Unknown category: ${category}`);
      return [];
    }

    return Object.entries(this.symbolMappings[category]).map(
      ([type, config]) => ({
        type,
        ...config,
      })
    );
  }

  /**
   * Get legend data for a specific symbol type
   * @param {string} symbolType - Symbol type (e.g., 'acute_toxicity', 'bees')
   * @returns {Object|null} Symbol configuration or null if not found
   */
  getSymbolConfig(symbolType) {
    // Search in all categories
    for (const category of Object.values(this.symbolMappings)) {
      if (category[symbolType]) {
        return { type: symbolType, ...category[symbolType] };
      }
    }

    console.warn(`Unknown symbol type: ${symbolType}`);
    return null;
  }

  /**
   * Get proper display info for a symbol label
   * @param {string} label - Symbol label from backend
   * @returns {Object} Display information
   */
  getSymbolDisplay(label) {
    if (!label) {
      console.warn('No label provided for symbol display');
      return {
        label: 'Unknown',
        image: faible,
        color: '#gray',
        order: 0,
      };
    }

    // Normalize label for lookup (lowercase, trim)
    const normalizedLabel = String(label).toLowerCase().trim();

    if (this.labelMappings[normalizedLabel]) {
      return this.labelMappings[normalizedLabel];
    }

    // Fallback: return the label as-is with default styling
    console.warn(`Unknown symbol label: "${label}"`);
    return {
      label: String(label),
      image: faible,
      color: '#gray',
      order: 0,
    };
  }

  /**
   * Process treatment/product response data and extract symbol information
   * @param {Object} responseData - API response data
   * @param {string} category - Optional category filter ('health' or 'environment')
   * @returns {Array} Processed symbol data
   */
  processSymbolData(responseData, category = null) {
    const symbols = [];
    const symbolTypes = category
      ? this.getSymbolsByCategory(category)
      : [
          ...this.getSymbolsByCategory('health'),
          ...this.getSymbolsByCategory('environment'),
        ];

    symbolTypes.forEach((symbolConfig) => {
      const dataKey = symbolConfig.key;
      const symbolData = responseData[dataKey];

      if (symbolData) {
        const { calculated_value, symbol } = symbolData;
        const label = symbol?.label || String(calculated_value);
        const displayInfo = this.getSymbolDisplay(label);

        symbols.push({
          type: symbolConfig.type,
          category: symbolConfig.category,
          name: symbolConfig.name,
          calculatedValue: calculated_value,
          symbol: symbol || null,
          label: label,
          display: displayInfo,
          rawData: symbolData,
        });
      }
    });

    return symbols;
  }

  /**
   * Generate complete legend for display (all possible labels)
   * @returns {Array} Legend items sorted by order
   */
  generateLegend() {
    const uniqueItems = {};

    Object.values(this.labelMappings).forEach((item) => {
      if (!uniqueItems[item.label]) {
        uniqueItems[item.label] = item;
      }
    });

    return Object.values(uniqueItems).sort((a, b) => a.order - b.order);
  }

  /**
   * Get health symbols from response data
   * @param {Object} responseData - API response
   * @returns {Array} Health symbol data
   */
  getHealthSymbols(responseData) {
    return this.processSymbolData(responseData, 'health');
  }

  /**
   * Get environment symbols from response data
   * @param {Object} responseData - API response
   * @returns {Array} Environment symbol data
   */
  getEnvironmentSymbols(responseData) {
    return this.processSymbolData(responseData, 'environment');
  }

  /**
   * Get all available symbol types
   * @returns {Array} All symbol types
   */
  getAllSymbolTypes() {
    return [
      ...this.getSymbolsByCategory('health'),
      ...this.getSymbolsByCategory('environment'),
    ];
  }

  /**
   * Check if a symbol type exists
   * @param {string} symbolType - Symbol type to check
   * @returns {boolean} True if exists
   */
  hasSymbolType(symbolType) {
    return this.getSymbolConfig(symbolType) !== null;
  }

  /**
   * Get risk level order for sorting (based on label)
   * @param {Object} symbolData - Symbol data object
   * @returns {number} Order value for sorting
   */
  getSymbolOrder(symbolData) {
    if (symbolData.display && symbolData.display.order) {
      return symbolData.display.order;
    }
    return 0;
  }
}

const symbolLegendManager = new SymbolLegendManager();
export default symbolLegendManager;
export { SymbolLegendManager };
